import { ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { PETITION_URL } from "@/lib/content";

const SOURCES = [
  {
    name: "RIVM — GGD-richtlijn MMK Omgevingsgeluid: maatregelen",
    desc: "Noemt snelheidsverlaging van 100 naar 80 km/u als bronmaatregel met een indicatieve geluidsreductie van circa 2 dB.",
    url: "https://www.rivm.nl/ggd-richtlijn-mmk-omgevingsgeluid/advisering-ggd/maatregelen",
    type: "Richtlijn / indicatief effect",
  },
  {
    name: "RIVM — Over geluid",
    desc: "Algemene wetenschappelijke kennis over verkeersgeluid, gezondheid en de logaritmische decibelschaal.",
    url: "https://www.rivm.nl/geluid/over-geluid",
    type: "Algemene wetenschappelijke kennis",
  },
  {
    name: "Rijkswaterstaat — Geluid langs rijkswegen",
    desc: "Uitleg over maatregelen tegen verkeersgeluid langs rijkswegen, waaronder stil asfalt (ZOAB) en geluidsschermen.",
    url: "https://www.rijkswaterstaat.nl/leefomgeving/leefbaarheid-en-milieu/milieukwaliteit/geluid-langs-rijkswegen",
    type: "Beheerdersinformatie",
  },
  {
    name: "Rijksoverheid — Geluidsoverlast van wegen",
    desc: "Officiële informatie over wegverkeerslawaai, regels en maatregelen.",
    url: "https://www.rijksoverheid.nl/onderwerpen/geluidsoverlast/geluidsoverlast-van-wegen",
    type: "Beleidsinformatie",
  },
  {
    name: "Europese Milieuagentschap (EEA) — Environmental noise in Europe 2025",
    desc: "Wegverkeer is de meest verspreide bron van transportlawaai in Europa; tientallen miljoenen Europeanen worden blootgesteld aan schadelijke geluidniveaus.",
    url: "https://www.eea.europa.eu/en/analysis/publications/environmental-noise-in-europe-2025",
    type: "Europees onderzoek",
  },
];

export const Sources = () => (
  <section id="bronnen" data-testid="sources-section" className="scroll-mt-24 bg-gooi-sand py-24 md:py-32">
    <div className="mx-auto max-w-[90rem] px-5 md:px-12">
      <Reveal>
        <Overline>Bronnen</Overline>
        <h2 className="mt-4 max-w-3xl font-display text-3xl font-medium leading-tight tracking-tight text-gooi-green md:text-5xl">
          Feitelijk onderbouwd
        </h2>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-gooi-green/75">
          We baseren ons uitsluitend op openbare, gezaghebbende bronnen. Daarbij maken we onderscheid tussen
          gemeten effecten, berekende effecten, algemene wetenschappelijke kennis, theoretische
          reistijdberekeningen en een indicatieve toepassing op het A1-traject.
        </p>
      </Reveal>

      <div className="mt-12 divide-y divide-gooi-green/10 border-y border-gooi-green/10">
        {SOURCES.map((s, i) => (
          <Reveal key={s.url} delay={i * 0.06}>
            <a
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`source-link-${i}`}
              className="group flex items-start justify-between gap-6 py-6 transition-colors duration-300 hover:bg-gooi-offwhite/60"
            >
              <div>
                <p className="font-display text-lg font-medium tracking-tight text-gooi-green md:text-xl">
                  {s.name}
                </p>
                <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-gooi-green/70">{s.desc}</p>
                <p className="mt-2 text-xs font-semibold uppercase tracking-[0.15em] text-gooi-forest/70">
                  {s.type}
                </p>
              </div>
              <ArrowUpRight
                className="mt-1 h-5 w-5 shrink-0 text-gooi-green/40 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-gooi-green"
                strokeWidth={1.5}
                aria-hidden="true"
              />
            </a>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <p className="mt-8 max-w-3xl text-xs leading-relaxed text-gooi-green/55">
          De reistijdvergelijking (±4 min 12 sec bij 100 km/u versus ±5 min 15 sec bij 80 km/u over ±7 km) is een
          eigen theoretische berekening bij constante snelheid. De petitie zelf staat op{" "}
          <a
            href={PETITION_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sources-petition-link"
            className="font-semibold text-gooi-green underline decoration-gooi-green/30 underline-offset-4 transition-colors duration-300 hover:decoration-gooi-green"
          >
            petities.nl
          </a>
          .
        </p>
      </Reveal>
    </div>
  </section>
);
